import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Wallet, IndianRupee, TrendingDown, FileSpreadsheet, Scale, Calculator, BookOpen, BarChart3, Download, ArrowRight } from "lucide-react";
import { PageHeader, StatCard, TxnBadge, Money, FilterSelect, useMeta } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { inr, fmtDate, fmtDateTime, monthLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ACTIONS = [
  { to: "/import", label: "Import Employees", icon: FileSpreadsheet, id: "import", roles: ["admin"] },
  { to: "/opening-balances", label: "Enter Opening Balance", icon: Scale, id: "opening", roles: ["admin", "payroll"] },
  { to: "/deduction", label: "Record Advance Deduction", icon: Calculator, id: "deduction", roles: ["admin", "payroll"], primary: true },
  { to: "/ledger", label: "View Employee Ledger", icon: BookOpen, id: "ledger" },
  { to: "/reports?tab=outstanding", label: "Outstanding Advance Report", icon: BarChart3, id: "outstanding" },
  { to: "/reports", label: "Export Reports", icon: Download, id: "export" },
];

function QuickActions() {
  const { can } = useAuth();
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-800 mb-4">Quick actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {ACTIONS.filter((a) => !a.roles || can(...a.roles)).map((a) => (
          <Link
            key={a.id}
            to={a.to}
            data-testid={`quick-action-${a.id}`}
            className={`group flex items-center gap-2.5 rounded-lg border p-3 text-sm font-medium transition-colors duration-150 ${a.primary ? "col-span-2 bg-blue-800 border-blue-800 text-white hover:bg-blue-900" : "border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50"}`}
          >
            <a.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{a.label}</span>
            <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function DeptBreakdown({ items }) {
  const max = Math.max(1, ...items.map((i) => i.outstanding));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="dept-breakdown">
      <h3 className="font-semibold text-slate-800 mb-4">Outstanding by department</h3>
      {items.length === 0 && <div className="text-sm text-slate-500">No outstanding advances.</div>}
      <div className="space-y-3">
        {items.slice(0, 8).map((d) => (
          <div key={d.department}>
            <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{d.department}</span><span className="money text-slate-800">{inr(d.outstanding)}</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-700 rounded-full" style={{ width: `${(d.outstanding / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentTable({ items }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="recent-transactions">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="font-semibold text-slate-800">Recent transactions</h3>
        <Link to="/reports?tab=ledger" className="text-xs text-blue-800 hover:underline">View all</Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Employee</th><th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-left hidden md:table-cell">Recorded</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-8">No transactions yet.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(t.date)}</td>
                <td className="px-4 py-2.5"><Link to={`/ledger/${t.employee_code}`} className="hover:text-blue-800"><span className="money text-xs text-blue-800">{t.employee_code}</span> <span className="whitespace-nowrap">{t.employee_name}</span></Link></td>
                <td className="px-4 py-2.5"><TxnBadge type={t.type} reversed={t.reversed} /></td>
                <td className={`px-4 py-2.5 text-right whitespace-nowrap ${t.credit ? "text-emerald-700" : "text-red-700"}`}><Money value={t.debit || t.credit} /></td>
                <td className="px-4 py-2.5 text-xs text-slate-500 hidden md:table-cell"><div className="whitespace-nowrap">{fmtDateTime(t.created_at)}</div><div className="truncate max-w-[140px]">{t.created_by}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const meta = useMeta();
  const [department, setDepartment] = useState("");
  const [branch, setBranch] = useState("");
  const [d, setD] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const params = { department, branch };
    Promise.all([api.get("/dashboard", { params }), api.get("/transactions/recent", { params: { ...params, limit: 10 } })])
      .then(([a, b]) => { setD(a.data); setRecent(b.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, [department, branch]);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Balaji Sweet House Private Limited · Employee advance overview"
        actions={
          <>
            {meta.departments.length > 0 && <FilterSelect value={department} onChange={setDepartment} options={meta.departments} placeholder="All departments" testid="dashboard-dept-filter" />}
            {meta.branches.length > 0 && <FilterSelect value={branch} onChange={setBranch} options={meta.branches} placeholder="All branches" testid="dashboard-branch-filter" />}
          </>
        }
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 fade-up">
        <StatCard label="Total employees" value={d ? d.total_employees : "—"} icon={Users} hint={d && `${d.active_employees} active`} testid="stat-total-employees" />
        <StatCard label="With outstanding" value={d ? d.employees_with_outstanding : "—"} icon={Wallet} tone="blue" testid="stat-with-outstanding" />
        <StatCard label="Total outstanding" value={d ? inr(d.total_outstanding) : "—"} icon={IndianRupee} tone="red" testid="stat-total-outstanding" />
        <StatCard label="Deductions this month" value={d ? inr(d.deductions_this_month) : "—"} icon={TrendingDown} tone="green" hint={d && `${monthLabel(d.month)} · ${d.deductions_this_month_count} entries`} testid="stat-deductions-month" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2"><RecentTable items={recent} /></div>
        <div className="space-y-4"><QuickActions /><DeptBreakdown items={d?.by_department || []} /></div>
      </div>
    </div>
  );
}
